import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BellRing, Loader2, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Pill } from "@/components/Badges";
import { api, errMsg, fmtDateTime } from "@/lib/api";

const EVENTS = [
  ["new_order", "New order placed"],
  ["new_client", "New client waiting for approval"],
  ["new_request", "New peptide request"],
  ["low_stock", "Product or BAC water running low"],
];

function TelegramRow({ s, reload }) {
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!waiting) return undefined;
    let tries = 0;
    timer.current = setInterval(async () => {
      tries += 1;
      try {
        await api.post("/admin/alerts/telegram/verify");
        clearInterval(timer.current);
        setWaiting(false);
        setLink("");
        toast.success("Telegram connected — check Telegram for a welcome message");
        reload();
      } catch {
        if (tries >= 40) {
          clearInterval(timer.current);
          setWaiting(false);
        }
      }
    }, 4000);
    return () => clearInterval(timer.current);
  }, [waiting, reload]);
  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const connect = () => run(async () => {
    const win = window.open("", "_blank");
    let data;
    try {
      ({ data } = await api.post("/admin/alerts/telegram/link"));
    } catch (e) {
      win?.close();
      throw e;
    }
    setLink(data.url);
    setWaiting(true);
    if (win) win.location.href = data.url;
  });
  const verify = () => run(async () => {
    await api.post("/admin/alerts/telegram/verify");
    clearInterval(timer.current);
    setWaiting(false);
    toast.success("Telegram connected");
    setLink("");
    reload();
  });
  const disconnect = () => run(async () => {
    await api.post("/admin/alerts/telegram/disconnect");
    reload();
  });

  if (!s.telegram_configured) {
    return <p className="text-xs text-amber-400" data-testid="telegram-not-configured">Telegram bot not set up yet — send your bot token in chat to finish setup.</p>;
  }
  if (s.telegram_connected) {
    return (
      <div className="flex items-center gap-2">
        <Pill color="emerald" testId="telegram-connected-badge">Connected · @{s.telegram_bot}</Pill>
        <Button size="sm" variant="ghost" onClick={disconnect} disabled={busy} data-testid="telegram-disconnect-button">Disconnect</Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={connect} disabled={busy} data-testid="telegram-connect-button">Connect Telegram</Button>
        {link && <Button size="sm" variant="outline" onClick={verify} disabled={busy} data-testid="telegram-verify-button">I tapped Start — check</Button>}
      </div>
      {link && (
        <p className="text-xs text-muted-foreground" data-testid="telegram-link-help">
          In Telegram, tap <b>Start</b> at the bottom of the chat.{" "}
          {waiting ? <span className="text-sky-400" data-testid="telegram-waiting">Waiting for your Start… this connects automatically.</span> : "Then press the check button."}{" "}
          Link didn't open? <a className="text-emerald-400 underline" href={link} target="_blank" rel="noreferrer">Open it here</a>.
        </p>
      )}
    </div>
  );
}

export function AlertsCard() {
  const [s, setS] = useState(null);
  const [testing, setTesting] = useState(false);
  const load = useCallback(() => api.get("/admin/alerts").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (key, val) => {
    try {
      const { data } = await api.put("/admin/alerts/preferences", { ...s.preferences, [key]: val });
      setS({ ...s, preferences: data });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/alerts/test");
      Object.entries(data).forEach(([k, v]) => (v === "sent" ? toast.success(`Test ${k} sent`) : toast.error(`${k} ${v}`)));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setTesting(false);
    }
  };

  if (!s) return null;
  const p = s.preferences;
  return (
    <div className="space-y-5 rounded-lg border border-border border-t-2 border-t-emerald-500 bg-card p-6 lg:col-span-2" data-testid="alerts-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-lg font-medium"><BellRing className="h-4 w-4 text-emerald-400" />Owner alerts</h2>
        <Button size="sm" variant="outline" onClick={test} disabled={testing} className="gap-2" data-testid="alerts-test-button">
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Send test alert
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-3 rounded-md border border-border p-4">
          <label className="flex items-center justify-between text-sm font-medium">
            <span className="flex items-center gap-2"><Send className="h-4 w-4 text-sky-400" />Telegram message</span>
            <Switch checked={p.telegram_enabled} onCheckedChange={(v) => toggle("telegram_enabled", v)} data-testid="alerts-telegram-switch" />
          </label>
          <TelegramRow s={s} reload={load} />
        </div>
        <div className="space-y-3 rounded-md border border-border p-4">
          <label className="flex items-center justify-between text-sm font-medium">
            <span className="flex items-center gap-2"><Mail className="h-4 w-4 text-sky-400" />Email</span>
            <Switch checked={p.email_enabled} onCheckedChange={(v) => toggle("email_enabled", v)} data-testid="alerts-email-switch" />
          </label>
          <p className="font-mono-data text-xs text-muted-foreground" data-testid="alerts-email-to">Sent to {s.email_to}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {EVENTS.map(([k, label]) => (
          <label key={k} className="flex items-center justify-between rounded-md border border-border px-4 py-2.5 text-sm">
            {label}
            <Switch checked={p[k]} onCheckedChange={(v) => toggle(k, v)} data-testid={`alerts-event-${k}-switch`} />
          </label>
        ))}
      </div>
      <div data-testid="alerts-log">
        <p className="mb-2 text-xs text-muted-foreground">Recent alerts</p>
        {s.log.length === 0 && <p className="text-xs text-muted-foreground">None yet.</p>}
        {s.log.map((l) => (
          <div key={l.id} className="flex flex-wrap justify-between gap-2 border-b border-border/50 py-1.5 text-xs">
            <span className="text-muted-foreground">{fmtDateTime(l.created_at)} · {l.title}</span>
            <span className="font-mono-data">
              {Object.entries(l.results).map(([k, v]) => <span key={k} className={`ml-2 ${v === "sent" ? "text-emerald-400" : "text-red-400"}`}>{k}: {v === "sent" ? "sent" : "failed"}</span>)}
              {Object.keys(l.results).length === 0 && <span className="text-muted-foreground">no channel on</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
